import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useContents } from "../../hooks/useContents";
import { ApiError } from "../../lib/api/client";
import { useToast } from "../../providers/ToastProvider";
import { Button } from "../ui/Button";
import { ColumnHeaders } from "./ColumnHeaders";
import { EmptyState } from "./EmptyState";
import { FileRow } from "./FileRow";
import { FolderRow } from "./FolderRow";
import { ListSkeleton } from "./ListSkeleton";
import type { ContentsResponse, FileDTO } from "../../lib/api/types";

interface ContentsListProps {
  folderId: string | null;
  onOpenFolder: (id: string) => void;
  onOpenPreview: (file: FileDTO) => void;
}

function FolderNotFound({ onBackHome }: { onBackHome: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-gutter py-16 text-center">
      <p className="text-body text-text-muted">Folder not found.</p>
      <Button variant="primary" onClick={onBackHome}>
        Back to Home
      </Button>
    </div>
  );
}

function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-gutter py-16 text-center">
      <p className="text-body text-danger-700">Couldn&apos;t load this folder.</p>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

export function ContentsList({ folderId, onOpenFolder, onOpenPreview }: ContentsListProps) {
  const navigate = useNavigate();
  const { push } = useToast();
  const query = useContents(folderId);
  const { data, isLoading, isError, error, isFetching, refetch } = query;

  // Surface 5xx load errors as a red toast (once per distinct error instance).
  const lastToastedError = useRef<unknown>(null);
  useEffect(() => {
    if (isError && error instanceof ApiError && error.status >= 500 && lastToastedError.current !== error) {
      lastToastedError.current = error;
      push({ kind: "error", message: error.message });
    }
    if (!isError) {
      lastToastedError.current = null;
    }
  }, [isError, error, push]);

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-lg border border-surface-border bg-surface">
        <ColumnHeaders />
        <ListSkeleton />
      </div>
    );
  }

  if (isError) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div className="overflow-hidden rounded-lg border border-surface-border bg-surface">
          <FolderNotFound onBackHome={() => navigate("/")} />
        </div>
      );
    }
    return (
      <div className="overflow-hidden rounded-lg border border-surface-border bg-surface">
        <LoadError onRetry={() => void refetch()} />
      </div>
    );
  }

  const contents = data as ContentsResponse;
  const isEmpty = contents.folders.length === 0 && contents.files.length === 0;

  return (
    <div className="overflow-hidden rounded-lg border border-surface-border bg-surface">
      <ColumnHeaders />
      <div
        className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"}
        aria-busy={isFetching}
      >
        {isEmpty ? (
          <EmptyState />
        ) : (
          <>
            {contents.folders.map((folder) => (
              <FolderRow
                key={folder.id}
                folder={folder}
                onOpen={onOpenFolder}
                disabled={isFetching}
              />
            ))}
            {contents.files.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                onOpenPreview={onOpenPreview}
                disabled={isFetching}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
